import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

export default function SSOCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [error, setError] = useState("");
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const errorParam = searchParams.get("error");

      if (errorParam) {
        setStatus("error");
        setError(decodeURIComponent(errorParam));
        return;
      }

      // The backend OAuth callback set the session_token cookie on the redirect
      // to this page. There is no token in the URL anymore (it used to be
      // ?token=... but that leaked via browser history / referer headers).
      // We just call /auth/me — the cookie travels via withCredentials.
      try {
        await loginWithToken(null);
        setStatus("success");
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } catch (err) {
        setStatus("error");
        setError(t("auth.sso.processError"));
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
      <Card className="w-full max-w-md mx-4">
        <CardContent className="py-12 text-center">
          {status === "processing" && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-primary" />
              <h2 className="text-xl font-semibold mb-2">{t("auth.sso.processing")}</h2>
              <p className="text-muted-foreground">{t("auth.sso.processingHint")}</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-xl font-semibold mb-2">{t("auth.sso.successTitle")}</h2>
              <p className="text-muted-foreground">{t("auth.sso.successHint")}</p>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-semibold mb-2">{t("auth.sso.errorTitle")}</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button onClick={() => navigate("/login")}>
                {t("auth.backToLogin")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
